import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import RenderRating from "../StarRating/RenderRating";
import { FaTimes } from "react-icons/fa";
import axiosInstance from "../../../axios";
import { getS3URL } from "../../../_helpers";
import { useNavigate, useParams } from "react-router-dom";
import "../Carousel/carousel.scss";
import { Star, CheckCircle2, MessageSquare } from "lucide-react";

const Carousel = ({ course, showWriteReview = true, isPurchased = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [dynamicSlides, setDynamicSlides] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);

  const handleWriteReviewClick = () => {
    const isLoggedIn = !!localStorage.getItem("token");
    if (isLoggedIn) {
      if (slug) {
        navigate(`/${slug}/userReview`, { state: { course } });
      } else {
        navigate(`/userReview`);
      }
    } else {
      navigate(`/login`, { state: { from: slug ? `/${slug}/userReview` : `/userReview`, course } });
    }
  };

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axiosInstance.get("/testimonials");
        if (response.data && !response.data.error) {
          const mapped = (response.data.data || [])
            .filter((row) => (row.status === 1 || row.status === "1") && !row.parentId)
            .map((row) => {
              let profileImg = `${process.env.REACT_APP_CDN_BASEURL || ''}/UI/testimonials/test_bt.png`;
              if (row.profile) {
                if (row.profile.startsWith('http')) {
                  profileImg = row.profile;
                } else {
                  profileImg = getS3URL(row.profile);
                }
              }
              return {
                profile: profileImg,
                testimonial: row.testimonial,
                name: row.name,
                courseName: row.company || row.designation || 'Course',
                subtitle: `Student of ${row.company || row.designation || 'Course'}`,
                rating: row.rating || "5"
              };
            });
          setDynamicSlides(mapped);
        }
      } catch (err) {
        console.error("Error fetching testimonials for Carousel:", err);
      }
    };
    fetchTestimonials();
  }, []);

  const courseName = course?.CourseName;
  const filteredSlides = React.useMemo(() => {
    if (!courseName) return dynamicSlides;
    return dynamicSlides.filter(
      (s) =>
        s.courseName?.toLowerCase().trim() === courseName.toLowerCase().trim()
    );
  }, [dynamicSlides, courseName]);

  const totalReviewsCount = filteredSlides.length;
  const averageRating = totalReviewsCount > 0
    ? (filteredSlides.reduce((sum, r) => sum + Number(r.rating || 5), 0) / totalReviewsCount).toFixed(1)
    : "5.0";

  const starBreakdown = React.useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    filteredSlides.forEach((r) => {
      const star = Math.round(Number(r.rating || 5));
      if (counts[star] !== undefined) {
        counts[star]++;
      }
    });
    return [5, 4, 3, 2, 1].map((stars) => ({
      stars,
      percentage: totalReviewsCount > 0 ? Math.round((counts[stars] / totalReviewsCount) * 100) : 0,
    }));
  }, [filteredSlides, totalReviewsCount]);

  const handleGoToReviews = (openWrite = false) => {
    const stateObj = { course, openWriteModal: openWrite };
    if (slug) {
      navigate(`/${slug}/userReview`, { state: stateObj });
    } else {
      navigate(`/userReview`, { state: stateObj });
    }
  };

  const track = [...dynamicSlides, ...dynamicSlides];

  return (
    <section className="carousel-wrapper">
      <div className={`testimonials-header-container ${!showWriteReview ? "no-feedback" : ""}`}>
        {showWriteReview && (
          <div className="testimonials-left">
            <div 
              onClick={() => handleGoToReviews(false)}
              className="cursor-pointer bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 max-w-md w-full hover:shadow-md transition-shadow hover:scale-[1.01] active:scale-95 duration-200 text-left"
            >
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">Student Feedback</h3>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-5xl font-black text-slate-900 tracking-tight">{averageRating}</span>
                  <span className="text-sm font-bold text-slate-400">/ 5</span>
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.round(Number(averageRating))
                            ? "fill-amber-400 text-amber-400"
                            : "text-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 font-bold tracking-wide">
                    ({totalReviewsCount} {totalReviewsCount === 1 ? "rating" : "ratings"})
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 pt-2">
                {starBreakdown.map((item) => (
                  <div
                    key={item.stars}
                    className="w-full flex items-center gap-3 text-xs text-slate-650 font-bold"
                  >
                    <span className="w-10 whitespace-nowrap flex items-center gap-1 font-bold">
                      {item.stars} <Star className="w-3 h-3 fill-amber-400 text-amber-400 inline-block" />
                    </span>
                    <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden relative">
                      <div
                        className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-amber-400 to-orange-400"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-slate-400 font-bold text-[10px]">{item.percentage}%</span>
                  </div>
                ))}
              </div>

              {isPurchased && (
                <div className="border-t border-slate-100 pt-6 bg-gradient-to-br from-indigo-50/20 via-transparent to-transparent -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl">
                  <h4 className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    Review this program
                  </h4>
                  <p className="text-[11px] text-slate-400 leading-relaxed mb-4 font-semibold">
                    Share your thoughts with other learners to help them find the right program.
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGoToReviews(true);
                    }}
                    className="w-full bg-slate-900 hover:bg-black text-white py-3 px-4 rounded-xl text-xs font-bold shadow-sm hover:shadow-md hover:shadow-indigo-500/5 active:scale-98 transition-all flex items-center justify-center gap-2 cursor-pointer border border-transparent"
                  >
                    <MessageSquare className="w-4 h-4 text-white/80" />
                    Write a product review
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="testimonials-right">
          <div className="testimonials-subtitle">Our Reviews</div>
          {/* <div className="testimonials-heading">{t("Testimonials Title")}</div> */}
        </div>
      </div>

      <div className="carousel-frame">
        <div className="marquee-track">
          {track.map((el, index) => (
            <div 
              key={index} 
              className="carousel-card"
              onClick={() => setSelectedReview(el)}
            >
              <div className="card-pic">
                <img src={el.profile} alt={el.name || "User Profile"} loading="lazy" />
              </div>
              <div className="card-course-name">{el.courseName}</div>
              <div className="card-student-name">{el.name}</div>
              <div className="star-div">
                <RenderRating ratings={el.rating} manageVotes={false} />
              </div>
              <p className="card-text">{el.testimonial}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Review Detail Modal */}
      {selectedReview && (
        <div 
          className="review-modal-backdrop"
          onClick={() => setSelectedReview(null)}
        >
          <div 
            className="review-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-accent-line" />

            <button 
              onClick={() => setSelectedReview(null)}
              className="modal-close-btn"
              aria-label="Close"
            >
              <FaTimes size={14} />
            </button>

            <div className="modal-profile-wrap">
              <img 
                src={selectedReview.profile} 
                alt={selectedReview.name || "User Profile"} 
                className="modal-profile-img"
              />
              <div className="modal-quote-badge">“</div>
            </div>

            <h3 className="modal-course-name">{selectedReview.courseName}</h3>
            <h4 className="modal-student-name">{selectedReview.name}</h4>

            <div className="modal-stars">
              <RenderRating ratings={selectedReview.rating} manageVotes={false} />
            </div>

            <div className="modal-testimonial-text">
              "{selectedReview.testimonial}"
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Carousel;
